import { API_BASE, type ChatChannelMemberResponse } from '@/lib/api';
import {
  CHAT_FILE_TYPE_POLICY,
  buildFileAcceptAttribute,
  isMimeInlineSafe,
  resolveFileType,
} from '@fias/platform-resources';

/**
 * Filters channel members for the @-mention typeahead: a case-insensitive
 * substring match over display name / username / nickname (Slack/Discord-style —
 * you can type either the handle or the shown name), excluding the current user.
 * An empty/whitespace query returns everyone (minus self). Pure so the composer's
 * static-filter path is unit-testable independent of the editor.
 */
export function filterChannelMembersByQuery(
  members: ChatChannelMemberResponse[],
  query: string,
  excludeUserId: string | null,
): ChatChannelMemberResponse[] {
  const q = query.trim().toLowerCase();
  return members.filter((m) => {
    if (m.userId === excludeUserId) return false;
    if (!q) return true;
    return (
      (m.displayName || m.userId).toLowerCase().includes(q) ||
      (m.username?.toLowerCase().includes(q) ?? false) ||
      (m.nickname?.toLowerCase().includes(q) ?? false)
    );
  });
}

// Channel labelling lives in lib/ so arches integrating with chat via its
// public API (e.g. customer-support's share-to-chat) can label channels without
// importing from this arche's directory. Re-exported here so chat-internal
// imports keep their natural path.
export { SELF_DM_LABEL, channelDisplayName } from '@/lib/chat-display';

// The file-type policy is shared platform code (`@fias/platform-resources`) and
// surface-neutral. Chat binds it to `CHAT_FILE_TYPE_POLICY` here so chat-internal
// callers don't repeat the policy argument at every call site.
export { CHAT_MESSAGE_MAX_ATTACHMENTS } from '@/lib/api';

/** Resolve a picked file to the canonical type chat will store, or a rejection. */
export function resolveChatAttachmentType(fileName: string, declaredMimeType: string) {
  return resolveFileType(fileName, declaredMimeType, CHAT_FILE_TYPE_POLICY);
}

/** Whether a STORED chat attachment mime may ever be rendered in-page. */
export function isChatAttachmentMimeInlineSafe(mimeType: string): boolean {
  return isMimeInlineSafe(mimeType, CHAT_FILE_TYPE_POLICY);
}

/**
 * `accept` for the composer's Document picker. Computed once — it is a pure
 * function of a frozen catalog, and it is read during render.
 *
 * Narrower than what chat actually accepts (the policy stores unrecognized
 * types opaquely), so drag-and-drop and the Vault picker stay open to formats
 * this string doesn't name. It exists to give the OS dialog a sensible default
 * filter, not to gate anything.
 */
export const CHAT_ATTACHMENT_DOCUMENT_ACCEPT = buildFileAcceptAttribute(CHAT_FILE_TYPE_POLICY, {
  excludeMedia: true,
});

/** Per-type chat send permissions, as resolved for the current member. */
export interface ChatAttachmentTypePermissions {
  canSendImages: boolean;
  canSendVideos: boolean;
  canSendAudio: boolean;
  canSendFiles: boolean;
}

/**
 * Whether the current member may attach a file of this MIME type, mirroring the
 * server's `mimeTypeToPermissionKey` exactly (image→images, video→videos,
 * audio→audio, everything else→files). Single source of truth so the composer's
 * drag-and-drop filter and the Vault picker never drift from each other or from
 * the server's `assertCanSendChatAttachment` gate.
 */
export function isChatAttachmentTypeAllowed(
  mimeType: string,
  perms: ChatAttachmentTypePermissions,
): boolean {
  const mainType = (mimeType || '').split('/')[0].toLowerCase();
  if (mainType === 'image') return perms.canSendImages;
  if (mainType === 'video') return perms.canSendVideos;
  if (mainType === 'audio') return perms.canSendAudio;
  return perms.canSendFiles;
}

/**
 * URL for a chat user's avatar image, served and presigned by the API and
 * keyed purely on userId. This lets the message list render any sender's
 * avatar — current user, other members, senders beyond the member-list page,
 * and WebSocket-delivered messages — without a members-list or payload
 * dependency. The endpoint 404s when the user has no avatar set; the caller
 * falls back to an initial.
 */
export function chatAvatarUrl(userId: string): string {
  return `${API_BASE}/arches/chat/users/${encodeURIComponent(userId)}/avatar`;
}

/**
 * Converts chat-message HTML (produced by the TipTap composer) into a single
 * line of plain text, suitable for compact previews — reply banners and quoted
 * snippets. Block boundaries (paragraphs, list items, line breaks) collapse to
 * spaces so words from adjacent blocks don't run together, and mention chips
 * reduce to their visible text.
 *
 * Previews must never render the raw HTML: doing so leaks literal tags (e.g.
 * "<p>") into the UI and lets long unbroken markup blow the layout past the
 * viewport edge.
 */
export function chatHtmlToPlainText(html: string): string {
  if (!html) return '';
  const withSpaces = html
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<\/(p|div|li|ul|ol|blockquote|pre)>/gi, ' ');
  const tmpl = document.createElement('template');
  tmpl.innerHTML = withSpaces;
  return (tmpl.content.textContent || '').replace(/\s+/g, ' ').trim();
}

/**
 * Converts chat-message HTML into plain text for the clipboard. Unlike
 * {@link chatHtmlToPlainText} (which flattens to one line for previews), this
 * preserves the message's line structure — block boundaries (paragraphs, list
 * items, <br>) become newlines — while trimming leading/trailing whitespace so
 * a copied message never carries stray blank lines ("paragraph marks") at the
 * start or end, and never leaks raw tags like "<p>".
 */
export function chatHtmlToCopyText(html: string): string {
  if (!html) return '';
  const withBreaks = html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li|ul|ol|blockquote|pre)>/gi, '\n');
  const tmpl = document.createElement('template');
  tmpl.innerHTML = withBreaks;
  const text = tmpl.content.textContent || '';
  return text
    .replace(/[ \t]+/g, ' ') // collapse horizontal whitespace runs
    .replace(/[ \t]*\n[ \t]*/g, '\n') // trim spaces hugging newlines
    .replace(/\n{3,}/g, '\n\n') // cap consecutive blank lines
    .trim();
}

/**
 * Derives a consistent two-stop gradient from a string seed.
 * Uses a djb2-style hash so the same name always produces the same colors.
 */
export function seededGradient(seed: string): string {
  let hash = 5381;
  for (let i = 0; i < seed.length; i++) {
    hash = ((hash << 5) + hash) ^ seed.charCodeAt(i);
  }
  hash = hash >>> 0;
  const h1 = hash % 360;
  const h2 = (h1 + 50 + ((hash >> 12) % 60)) % 360;
  return `linear-gradient(135deg, hsl(${h1}, 65%, 50%), hsl(${h2}, 65%, 40%))`;
}
